import type { Restaurant } from 'hooks/use-unit';
import Cookies from 'js-cookie';
import uuid from 'uniqid';
import {
  GET_IN_ANONYMOUS_ID,
  notFoundUnitImages,
  REGULAR_EXPRESSIONS,
} from './constants';
import { Cuisine } from 'hooks/use-cuisines';

export const isBirthdayValid = (value: string | null | undefined) => {
  if (value && value.length === 10) {
    const birthday = new Date(value.split('/').reverse().join('-'));

    if (birthday.toString() !== 'Invalid Date') {
      return true;
    }
  }

  return false;
};

export const getValidDate = (date: string) => {
  return date.split('/').reverse().join('-');
};

export const getFormattedDate = (date: string) => {
  const parsedDate = new Date(date.includes('T') ? date : date + 'T00:00:00');

  const day = parsedDate.getDate();
  const month = parsedDate.getMonth() + 1;
  const year = parsedDate.getFullYear();

  const formattedDate = `${day >= 1 && day <= 9 ? `0${day}` : day}/${
    month >= 1 && month <= 9 ? `0${month}` : month
  }/${year}`;

  return formattedDate;
};

export const getStringIgnoringAccents = (text: string) =>
  text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export const getPrice = (price: number) =>
  price.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' });

export const getDistance = (distance: number) => {
  if (distance < 1) return 1;
  if (distance > 50) return 50;

  return distance;
};

export const getPhoneNumberFormatted = (
  phoneNumber: string | number,
  resultType: 'withCountry' | 'withoutCountry'
) => {
  const phoneCleared = `${String(phoneNumber)}`.replace(/\D/g, '');
  const match = phoneCleared.match(REGULAR_EXPRESSIONS.fullPhoneNumber);

  if (resultType === 'withCountry') {
    return `+${match?.[1]} (${match?.[2]}) ${match?.[3]}-${match?.[4]}`;
  } else {
    return `(${match?.[2]}) ${match?.[3]}-${match?.[4]}`;
  }
};

export const getVariantColor = (price: string, range: string) => {
  return price === range ? 'primary' : 'outline';
};

export const getRandomItem = <T>(allItems: T[]): T => {
  return allItems[Math.floor(Math.random() * allItems.length)];
};

export const getCoverImgFromUnit = (restaurant: Restaurant): string => {
  return (
    restaurant?.cover_image ||
    restaurant?.photos?.[0]?.image ||
    getRandomItem(notFoundUnitImages)
  );
};

type EventProps = {
  [key: string]: unknown;
};

export const Event = {
  push: (name: string, props?: EventProps) => {
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];

      let anonymous_id = Cookies.get(GET_IN_ANONYMOUS_ID);
      if (!anonymous_id) {
        anonymous_id = uuid();
        Cookies.set(GET_IN_ANONYMOUS_ID, anonymous_id);
      }

      const params: EventProps = {
        event: name,
        anonymous_id,
        ...props,
      };

      if (props?.diner_id || props?.user_id) {
        delete params.anonymous_id;
      }

      window.dataLayer.push(params);
    }
  },
};

export const jsonToQueryString = (json: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}) => {
  const params: string[] = [];

  Object.keys(json).forEach(function (key) {
    return (
      ['string', 'number', 'boolean'].includes(typeof json[key]) &&
      params.push(`${encodeURIComponent(key)}=${encodeURIComponent(json[key])}`)
    );
  });

  return `?${params.join('&')}`;
};

export const isValidLinkToImage = (link: string | null | undefined) => {
  if (typeof link !== 'string') return false;

  const linkSplit = link.split('.');

  return ['jpg', 'png', 'jpeg', 'webp', 'avif'].includes(
    linkSplit[linkSplit.length - 1]
  );
};

const LOCALIZED_FLAG_REGEX = /\[[a-z]{2}-[a-z]{2}\]/gi;
const PTBR_FLAG = '[pt-br]';

export const getPtBrLocalizedText = (text: string | null | undefined) => {
  if (!text) {
    return '';
  }

  const matches = Array.from(text.matchAll(LOCALIZED_FLAG_REGEX));

  if (matches.length === 0) {
    return text;
  }

  const ptBrIndex = matches.findIndex(
    (match) => match[0].toLowerCase() === PTBR_FLAG
  );

  if (ptBrIndex === -1) {
    return '';
  }

  const ptBrMatch = matches[ptBrIndex];
  const start = (ptBrMatch.index ?? 0) + ptBrMatch[0].length;
  const end = matches[ptBrIndex + 1]?.index ?? text.length;

  return text.slice(start, end).trim();
};

export const isLastItem = (index: number, array: unknown[]) => {
  return index === array.length - 1;
};

export const getMaximumLimitListCuisines = (cuisines: Cuisine[]) => {
  if ((cuisines || null) === null) {
    return '';
  }

  let cuisineList = '';

  cuisines.slice(0, 3).forEach((cuisine, index, array) => {
    cuisineList += `${cuisine.name}${isLastItem(index, array) ? '' : ', '}`;
  });

  if (cuisines.length > 3) {
    cuisineList += '...';
  }

  return cuisineList;
};
